/*
 * FAST PASS (2026-10-20) — in-modal bypass mint + solve.
 *
 * WHY THIS FILE EXISTS. kohlcash's activation flow is page-bound: the captcha
 * popup hands off to /addon.js/hashcash?action=get (a whole new page), that page
 * POSTs a <form> and the solved state is a THIRD page (getHashcashSolved) telling
 * the user to "save this link". Measured operator walk-through 2026-10-20: modal
 * -> new tab -> solve page -> congrats page -> back to the original tab, where the
 * modal is STILL OPEN saying "Almost there!" (postCommon.js left it open with no
 * cross-tab signal), -> link again. That is the "scammy ringer" the operator
 * refused to ship.
 *
 * This module runs the SAME engine kohlcash's page runs — same worker file, same
 * argon2, same two endpoints — inside the captcha modal, then closes the modal and
 * resumes the post. Nothing here is a new contract:
 *   GET  /addon.js/hashcash?action=get&json=1   -> {solved, hash, difficulty}
 *   POST addon.js/hashcash?action=solve (json)  -> 'ok'  (sets bypass+extraCookie)
 * Both shapes and the worker message protocol are taken verbatim from the proven
 * page-side code in hashcash.js (getParameters / startWorker / onMessageFunction /
 * sendSolution), so the wire behaviour is identical to what is VM-proven.
 *
 * VOCABULARY RULE (operator ruling 2026-10-20): the user-facing surface says
 * "Fast Pass" and nothing else. No captcha/PoW/hashcash/bypass/miner/crypto words
 * on any user surface — the admin UI keeps the real names on purpose. Cost is
 * disclosed BEFORE the work starts (that is what separates a disclosed CPU cost
 * from a "2026 CPU miner" surprise), and the success line never asks the user to
 * save, bookmark or remember anything: the pass rides the cookie and is re-minted
 * by the same one-check flow.
 */
var fastpass = {};

fastpass.getPath = '/addon.js/hashcash?action=get&json=1';
fastpass.solvePath = 'addon.js/hashcash?action=solve';
fastpass.workerPath = '/.static/js/hashcash_worker.js';

fastpass.vars = null;
fastpass.workers = [];
fastpass.workDone = [];
fastpass.workerRanges = [];
fastpass.intervals = [];
fastpass.running = false;
fastpass.begun = false;
fastpass.ui = null;

/* ---- capability probe (mirrors hashcash.isBrowserCompatible) ---- */

fastpass.supported = function() {
  return !!window.Worker
      && (typeof WebAssembly === 'object')
      && (typeof WebAssembly.instantiate === 'function');
};

/* ---- parameters (mirrors hashcash.getParameters) ---- */

fastpass.getParameters = function(callback) {

  api.localRequest(fastpass.getPath, function gotHashcash(error, data) {

    if (error) {
      return callback(error);
    }

    var dataObject = JSON.parse(data);

    if (dataObject.status !== 'ok') {
      return callback(dataObject.status);
    }

    if (dataObject.data.solved === true) {
      return callback('solved');
    }

    callback(null, dataObject.data);

  });

};

/* ---- worker plumbing (mirrors hashcash.startWorker + onMessageFunction) ---- */

fastpass.startWorker = function(pid, from, to) {

  fastpass.workerRanges.push({ from: from, to: to });

  var worker = new Worker(fastpass.workerPath);

  worker.onmessage = function(resp) {

    if (resp.data['event'] == 'progress') {

      fastpass.workDone[pid] = resp.data['value'];

    } else if (resp.data['event'] == 'finished') {

      fastpass.stop();
      fastpass.sendSolution(resp.data['value'], 0);

    } else if (resp.data['event'] == 'start') {

      worker.postMessage(
          [fastpass.vars.hash, from, to]);

    }

  };

  fastpass.workers.push(worker);
  fastpass.workDone.push(0);

};

fastpass.workSum = function() {

  var sum = 0;

  for (var i = 0; i < fastpass.workDone.length; i++) {
    sum += fastpass.workDone[i];
  }

  return sum;

};

/* ---- solution submission (mirrors hashcash.sendSolution, minus location.reload) ---- */

fastpass.sendSolution = function(value, sendCount) {

  api.formApiRequest(fastpass.solvePath, {
    secret: value
  }, function requestComplete(status) {

    if (status === 'ok') {

      fastpass.ui.onDone();

    } else if (status === 'fail') {

      // The page version retried silently every 10s behind a progress headline.
      // In-modal the user must SEE why it is waiting, so the line is user-facing.
      fastpass.ui.onText(lang.fastpassRetrying);

      setTimeout(function() {
        fastpass.sendSolution(value, sendCount + 1);
      }, 10000);

    } else {

      fastpass.ui.onFail(status);

    }

  }, true, null, true);

};

/* ---- the one entry point ---- */

/*
 * fastpass.mint(ui)
 *   ui.onText(text)      - replace the status line
 *   ui.onProgress(pct)   - 0..100
 *   ui.onDone()          - pass is live (bypass cookie is set by the solve call)
 *   ui.onFail(status)    - cannot continue in-browser; ui shows the page fallback
 */
fastpass.mint = function(ui) {

  fastpass.ui = ui;
  fastpass.begun = true;

  fastpass.getParameters(function(status, parameters) {

    if (status === 'solved') {
      // Already minted (e.g. a second intent on the same page).
      fastpass.ui.onDone();
      return;
    }

    if (status) {
      fastpass.ui.onFail(status);
      return;
    }

    if (!fastpass.supported()) {
      fastpass.ui.onFail('unsupported');
      return;
    }

    fastpass.vars = parameters;

    var maxWorkers = navigator.hardwareConcurrency || 4;
    var workerCount = Math.max(1, Math.floor(maxWorkers / 2));

    // Same split arithmetic as hashcash.beginWork.
    var perWorker = Math.floor(fastpass.vars.difficulty / workerCount);
    var i;

    for (i = 0; i < workerCount; i++) {
      var from = perWorker * i + (i > 0 ? 1 : 0);
      var to = i != workerCount - 1 ? perWorker * (i + 1)
          : fastpass.vars.difficulty - 1;
      fastpass.startWorker(i, from, to);
    }

    fastpass.running = true;
    fastpass.startDate = new Date();
    fastpass.intervals.push(setInterval(fastpass.updateProgress, 250));

  });

};

fastpass.updateProgress = function() {

  var percentage = (fastpass.workSum() / fastpass.vars.difficulty) * 100;

  fastpass.ui.onProgress(percentage);

};

fastpass.stop = function() {

  for (var i = 0; i < fastpass.intervals.length; i++) {
    clearInterval(fastpass.intervals[i]);
  }

  fastpass.intervals = [];

  for (i = 0; i < fastpass.workers.length; i++) {
    fastpass.workers[i].terminate();
  }

  fastpass.workers = [];
  fastpass.running = false;

};

/* ---- bypass status probe (the same call board.js/thread.js already make) ---- */

fastpass.checkStatus = function(callback) {

  api.formApiRequest('blockBypass', {}, function checked(status, data) {
    callback(status === 'ok' ? data : null);
  });

};

/*
 * INTENT TRIGGER (operator ruling 2026-10-20): opening the posting form — the
 * quick-reply panel, or the first focus of the comment field on a page whose form
 * ships open — is the signal that says "this visitor means to post". At that point
 * the gate is shown, once per page view, instead of after the Post button bounces.
 * The gesture itself costs a bot nothing; the toll is the argon2 mint, which is why
 * this is a trigger and not a gate.
 */
fastpass.armed = false;

fastpass.onIntent = function() {

  if (fastpass.armed || typeof postCommon === 'undefined') {
    return;
  }

  fastpass.armed = true;

  fastpass.checkStatus(function(data) {

    if (!data) {
      return;
    }

    // OPERATOR RULING 2026-10-21 (trusted-pass doctrine, issue 14 + the (b)
    // ruling on the intent trigger): this trigger arms ONLY at bypassMode 2.
    // On mode-1 boxes (live) it is inert for EVERY user class — the modal's
    // timing for alwaysUseBypass pass-holders stays exactly as board.js/
    // thread.js ship it at submit time, and valid passes pay nothing anywhere
    // (a pass is a bypass doc, so data.valid short-circuits it here too).
    if (data.mode == 2 && !data.valid) {
      postCommon.displayBlockBypassPrompt(function() {});
    }

  });

};

fastpass.init = function() {

  // Board/catalog pages ship the posting form open: first focus = intent.
  var field = document.getElementById('fieldMessage');

  if (field) {
    field.addEventListener('focus', function() {
      fastpass.onIntent();
    }, { once: true });
  }

};

fastpass.init();
